import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface FavoriteButtonProps {
  artworkId: string;
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const FavoriteButton = ({ artworkId, size = "icon", className }: FavoriteButtonProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any>(null);

  // Get current user
  supabase.auth.getSession().then(({ data: { session } }) => {
    setUser(session?.user || null);
  });

  // Check if artwork is favorited
  const { data: isFavorite } = useQuery({
    queryKey: ["favorite", artworkId, user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("artwork_id", artworkId)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!user,
  });

  // Toggle favorite mutation
  const toggleFavorite = useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error(t('auth.mustBeLoggedIn'));
      }

      if (isFavorite) {
        // Remove favorite
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("artwork_id", artworkId);

        if (error) throw error;
      } else {
        // Add favorite
        const { error } = await supabase
          .from("favorites")
          .insert({
            user_id: user.id,
            artwork_id: artworkId,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorite", artworkId, user?.id] });
      toast({
        title: isFavorite ? t('favorites.removed') : t('favorites.added'),
        description: isFavorite 
          ? t('favorites.removedDescription')
          : t('favorites.addedDescription'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('favorites.error'),
        description: error.message || t('favorites.errorDescription'),
        variant: "destructive",
      });
    },
  });

  if (!user) return null;

  return (
    <Button
      size={size}
      variant="ghost"
      aria-label={isFavorite ? t('favorites.remove') : t('favorites.add')}
      className={cn(
        "hover:scale-110 transition-all duration-300",
        isFavorite && "text-red-500 hover:text-red-600",
        className
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite.mutate();
      }}
    >
      <Heart 
        className={cn(
          "h-5 w-5",
          isFavorite && "fill-current"
        )} 
      />
    </Button>
  );
};